import React from 'react';
import styled from 'styled-components';

import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';
import store, { useAppDispatch } from '@/store';
import { sessionSave } from '@/store/slices/session';

import type {
  Campaign,
  ConfiguratorContextEngagementBundle,
} from '@/components/modules/spark/configurator/types';

import { BenchmarkBar } from './components/BenchmarkBar';
import { CompetitorGrid } from './components/CompetitorGrid';
import { FeaturePack } from './components/FeaturePack';
import { GradientWaves } from './components/GradientWaves';
import { HeroStar } from './components/HeroStar';
import { PackagesDisplay } from './components/PackagesDisplay';
import { ProgressBar } from './components/ProgressBar';
import { ScanHeader } from './components/ScanHeader';
import { SelectedPackageFooter } from './components/SelectedPackageFooter';
import { DEFAULT_CONFIG, getCompetitorsForCountry, getMarketScanTimingsForCountry } from './constants';
import { useAnimationEngine, useRecommendedCampaigns, resetMarketScanAnimation, isMarketScanAnimationFinished } from './hooks';
import { MarketScanConfig, MarketScanContextValue, MarketScanProps, PackageConfig } from './types';

import './styles.scss';

const getText = (textObj: any, lang: string = 'en'): string => {
  if (typeof textObj === 'string') return textObj;
  if (!textObj) return '';
  return textObj[lang] || textObj['en'] || Object.values(textObj)[0] || '';
};


const isBasicCampaign = (campaign: Campaign, lang: string): boolean =>
  getText(campaign.name, lang).trim().toLowerCase() === 'basic';

const mapCampaignsToPackages = (
  campaigns: Campaign[],
  lang: string = 'en',
  confirmedCampaignId?: string | number,
  isMobile: boolean = false,
): PackageConfig[] => {
  // The "Basic" campaign is mobile-only — drop it from the desktop package list.
  const sourceCampaigns = isMobile ? campaigns : campaigns.filter((c) => !isBasicCampaign(c, lang));

  const packages: PackageConfig[] = sourceCampaigns.map((campaign, index) => ({
    id: String(campaign.id),
    name: getText(campaign.name, lang),
    description: getText(campaign.description, lang),
    isBasic: isBasicCampaign(campaign, lang),
    price: campaign.price_evaluation.discountedPrice > 0
      ? campaign.price_evaluation.discountedPrice
      : campaign.price_evaluation.basePrice,
    originalPrice:
      campaign.price_evaluation.discountedPrice > 0 &&
      campaign.price_evaluation.basePrice > campaign.price_evaluation.discountedPrice
        ? campaign.price_evaluation.basePrice
        : undefined,
    currency: campaign.currency_code,
    period: '',
    label: undefined,
    isSelected: confirmedCampaignId !== undefined
      ? String(campaign.id) === String(confirmedCampaignId)
      : false,
    revealOrder: index,
  }));

  // Basic is mobile-only. Ensure it exists: if the backend hasn't returned a
  // "Basic" campaign yet, inject a hardcoded placeholder (free, no gamification
  // features — the card shows "Included" via isBasic). When nothing is confirmed,
  // surface Basic first and preselect it instead of the backend order.
  if (isMobile) {
    let basicIndex = packages.findIndex((p) => p.isBasic);
    if (basicIndex === -1) {
      packages.unshift({
        id: 'basic',
        name: 'Basic',
        description: '',
        isBasic: true,
        price: 0,
        currency: packages[0]?.currency ?? 'USD',
        period: '',
        label: undefined,
        isSelected: false,
        revealOrder: 0,
      });
      basicIndex = 0;
    }
    if (confirmedCampaignId === undefined && basicIndex > -1) {
      const [basic] = packages.splice(basicIndex, 1);
      basic.isSelected = true;
      packages.unshift(basic);
    }
    packages.forEach((p, i) => {
      p.revealOrder = i;
    });
  }

  return packages;
};

/** Shared per-item computation given a resolved active tier and icon. */
function resolveFeaturePackEntry(
  itemName: any,
  tiers: any[],
  activeTier: any | undefined,
  icon: string | undefined,
  getTextLocal: (textObj: any) => string,
): import('./types').FeaturePackItemConfig {
  const levelTiers = tiers.filter((t: any) => !t.name?.toLowerCase().includes('off'));
  const totalLevels = levelTiers.length;
  const activeLevelTier = levelTiers.find((t: any) => t.id === activeTier?.id);
  const currentLevel = activeLevelTier
    ? levelTiers.findIndex((t: any) => t.id === activeLevelTier.id) + 1
    : 0;
  const progress = totalLevels > 0 ? Math.round((currentLevel / totalLevels) * 100) : 0;
  const marketTierIndex = Math.max(0, Math.floor((totalLevels - 1) / 2));
  const marketTier = levelTiers[marketTierIndex];
  const marketLevel = marketTierIndex + 1;
  const marketPin = totalLevels > 0 ? Math.round((marketLevel / totalLevels) * 100) : 0;
  const ahead = currentLevel > marketLevel;
  const behind = currentLevel < marketLevel;
  const label = getTextLocal(itemName);
  const pinLabel = getTextLocal(activeTier?.name) ?? '';
  let pinLevel = pinLabel;
  if (label && pinLabel.indexOf(label) === 0) {
    pinLevel = pinLabel.slice(label.length).replace(/^[\s\-:]+/, '');
  } else if (pinLabel) {
    const parts = pinLabel.trim().split(/\s+/);
    pinLevel = parts[parts.length - 1] ?? '';
  }
  return {
    label,
    icon,
    level: totalLevels > 0 ? `Level ${currentLevel}/${totalLevels}` : '',
    progress,
    marketPin,
    marketPinLabel: getTextLocal(marketTier?.name) ?? '',
    pinLabel,
    pinLevel,
    comparison: ahead ? 'Ahead of market' : behind ? 'Below market' : 'Market competitive',
  };
}

/** Compute feature pack items for a given bundle. Items have tiers embedded. */
function computeFeaturePackItems(
  engagementItems: any[],
  bundle: ConfiguratorContextEngagementBundle | undefined,
  fallback: import('./types').FeaturePackItemConfig[],
  lang: string = 'en',
): import('./types').FeaturePackItemConfig[] {
  if (!engagementItems.length) return fallback;

  const bundleTierIds = new Set<number>();
  (bundle?.bundle_items ?? []).forEach((bi: any) => {
    const tierId = bi.discounted_cost?.gamification_tier_id;
    if (tierId != null) bundleTierIds.add(tierId);
  });

  const getTextLocal = (textObj: any): string => {
    if (typeof textObj === 'string') return textObj;
    if (!textObj) return '';
    return textObj[lang] || textObj['en'] || Object.values(textObj)[0] || '';
  };

  return engagementItems
    .filter((item: any) => item.is_active)
    .sort((a: any, b: any) => a.priority - b.priority)
    .map((item: any) => {
      const allTiersSorted: any[] = (item.tiers ?? [])
        .filter((t: any) => t.is_active)
        .sort((a: any, b: any) => a.priority - b.priority);
      const activeTier: any =
        allTiersSorted.find((t: any) => bundleTierIds.has(t.id)) ??
        allTiersSorted.find((t: any) => t.active);
      return resolveFeaturePackEntry(item.name, allTiersSorted, activeTier, item.art_bundle?.[0]?.logo, getTextLocal);
    });
}

/** Compute feature pack items directly from a recommended Campaign. */
function computeFeaturePackFromCampaign(
  campaign: Campaign,
  engagementItems: any[],
  fallback: import('./types').FeaturePackItemConfig[],
  lang: string = 'en',
): import('./types').FeaturePackItemConfig[] {
  if (!campaign.items?.length) return fallback;

  const defaultTierByItemId = new Map<number, number | string>();
  (campaign.default_items ?? []).forEach((di) => {
    defaultTierByItemId.set(di.item_id, di.tier_id);
  });

  const engItemMap = new Map<number, any>();
  (engagementItems as any[]).forEach((ei) => engItemMap.set(ei.id, ei));

  const getTextLocal = (textObj: any): string => {
    if (typeof textObj === 'string') return textObj;
    if (!textObj) return '';
    return textObj[lang] || textObj['en'] || Object.values(textObj)[0] || '';
  };

  return campaign.items
    .filter((item) => item.type === 'GAMIFICATION_ITEM')
    .sort((a, b) => a.priority - b.priority)
    .reduce<import('./types').FeaturePackItemConfig[]>((acc, item) => {
      const defaultTierId = defaultTierByItemId.get(item.id);
      if (defaultTierId === undefined) return acc; // not in this pack
      const activeTier = (item.tiers ?? []).find((t) => String(t.id) === String(defaultTierId));
      if (!activeTier || activeTier.name?.toLowerCase().includes('off')) return acc;
      const engItem = engItemMap.get(item.id);
      acc.push(resolveFeaturePackEntry(item.name, item.tiers ?? [], activeTier, engItem?.art_bundle?.[0]?.logo, getTextLocal));
      return acc;
    }, []);
}

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const PROGRESS_STAGES = new Set(['intro', 'ui-init', 'model-prep', 'analyzing', 'preparing-packs']);
const COMPETITOR_STAGES = new Set(['analyzing', 'preparing-packs', 'display-packs']);
const PACKAGE_STAGES = new Set(['intro', 'ui-init', 'model-prep', 'analyzing', 'preparing-packs', 'display-packs']);

const MarketScan = (componentProps: MarketScanProps) => {
  const dispatch = useAppDispatch();
  const parentContext = React.useContext(DataElementContext);
  const [props] = processComponentProps(componentProps, parentContext);
  const { className, styleText, properties } = props as MarketScanProps;
  const renderUi = properties?.renderUi !== false;

  const engagementBundles: ConfiguratorContextEngagementBundle[] =
    (parentContext as any)?.engagementBundles ?? [];
  const engagementItems: any[] = React.useMemo(
    () => (parentContext as any)?.engagementItems ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [(parentContext as any)?.engagementItems],
  );
  const onEngagementSelectModalToggle: (() => void) | undefined = (parentContext as any)
    ?.onEngagementSelectModalToggle;

  const country = (parentContext as any)?.country;
  const countryCode: string | undefined = country?.code;
  const lang = (parentContext as any)?.language?.code || 'en';
  const confirmedCampaignId: string | number | undefined = (parentContext as any)?.engagementCampaign?.id;
  const isMobile: boolean = !!(parentContext as any)?.isMobile;

  const baseConfig: MarketScanConfig = (parentContext as any)?.marketScanConfig ?? DEFAULT_CONFIG;

  // The active bundle is the one whose active flag is true (set by useEngagementStep)
  const selectedBundle: ConfiguratorContextEngagementBundle | undefined = engagementBundles.find(
    (b) => b.active,
  );

  const derivedFeaturePack = React.useMemo(
    () => computeFeaturePackItems(engagementItems, selectedBundle, baseConfig.featurePack),
    [engagementItems, selectedBundle, baseConfig.featurePack],
  );

  const { campaigns: recommendedCampaigns, setCampaign } = useRecommendedCampaigns(countryCode);

  const hasStartedRef = React.useRef(isMarketScanAnimationFinished() && recommendedCampaigns.length > 0);
  const onMarketScanPlayed: (() => void) | undefined = (parentContext as any)?.onMarketScanPlayed;

  // Reset animation only when countryCode actually changes (not on initial mount)
  const prevCountryCodeRef = React.useRef(countryCode);
  React.useEffect(() => {
    if (prevCountryCodeRef.current === countryCode) return;
    prevCountryCodeRef.current = countryCode;
    resetMarketScanAnimation();
    hasStartedRef.current = false;
  }, [countryCode]);

  const countryLocationText = getText(country?.art_bundle?.[0]?.text_name, lang) || country?.name || baseConfig.header.locationText;
  const countryLogoSrc: string | undefined = country?.art_bundle?.[0]?.logo || undefined;
  const countryCompetitors = getCompetitorsForCountry(countryCode, country?.name);
  const countryTimings = getMarketScanTimingsForCountry(countryCode, country?.name);

  const config = React.useMemo<MarketScanConfig>(
    () => ({
      ...baseConfig,
      timing: { ...baseConfig.timing, ...countryTimings.timing },
      stages: { ...baseConfig.stages, ...countryTimings.stages },
      header: {
        ...baseConfig.header,
        locationText: countryLocationText,
        countryLogoSrc,
      },
      competitors: countryCompetitors,
      packages: mapCampaignsToPackages(recommendedCampaigns, lang, confirmedCampaignId, isMobile),
      featurePack: derivedFeaturePack,
    }),
    [baseConfig, countryLocationText, countryLogoSrc, countryCompetitors, countryTimings, recommendedCampaigns, derivedFeaturePack, lang, confirmedCampaignId, isMobile],
  );

  const [packagesCompact, setPackagesCompact] = React.useState(false);
  const bodyRef = React.useRef<HTMLDivElement>(null);

  // Freeze the config passed to the animation engine once the animation starts.
  // Any subsequent config changes (e.g. from session updates cascading through
  // engagementBundles → derivedFeaturePack) are invisible to the engine while
  // it is running, preventing mid-animation glitches.
  const animationConfigRef = React.useRef<MarketScanConfig>(config);
  // Keep config current before animation starts AND after it finishes.
  // During the active animation (started but not done) the config is frozen to
  // prevent mid-animation glitches (e.g. feature-pack changes cascading in).
  // After finishing, quick-reveal needs an up-to-date `isSelected` flag so it
  // restores the user's confirmed campaign rather than the default first package.
  if (!hasStartedRef.current || isMarketScanAnimationFinished()) {
    animationConfigRef.current = config;
  }

  const {
    state,
    start,
    close: closeAnimation,
    selectPackage,
    openFeatureModal,
    closeFeatureModal,
    openPackageDrawer,
    closePackageDrawer,
    openConfigureScreen,
    closeConfigureScreen,
    selectConfigureFeature,
    selectConfigureTier,
    seedConfigureTier,
    clearConfigureSelections,
  } = useAnimationEngine(animationConfigRef.current);

  // Selecting a package highlights it locally; on mobile it also commits the
  // choice immediately via set-recommended-campaign (desktop commits on confirm).
  // The same handler backs the "Continue with <campaign>" CTA in the details
  // drawer. Guard against the synthetic "__configure__" card (and any other
  // non-campaign id) so we never POST a bogus campaign id.
  const handlePackageSelect = React.useCallback(
    (packageId: string) => {
      selectPackage(packageId);
      const isRealCampaign = recommendedCampaigns.some((c) => String(c.id) === String(packageId));
      if (isMobile && isRealCampaign) {
        setCampaign(packageId);
      }
    },
    [selectPackage, isMobile, setCampaign, recommendedCampaigns],
  );

  // Recompute feature pack live based on which recommended campaign the user selected.
  const liveFeaturePack = React.useMemo(() => {
    const targetId = state.selectedPackageId ?? recommendedCampaigns[0]?.id;
    const liveCampaign = targetId
      ? recommendedCampaigns.find((c) => String(c.id) === String(targetId))
      : undefined;
    if (liveCampaign) {
      return computeFeaturePackFromCampaign(liveCampaign, engagementItems, [], lang);
    }
    return [];
  }, [state.selectedPackageId, recommendedCampaigns, engagementItems, selectedBundle, baseConfig.featurePack, lang]);

  // liveBenchmark: campaigns don't carry benchmark data; use config.benchmark directly
  const liveBenchmark = config.benchmark;

  const close = React.useCallback(() => {
    // Reset start flag and unfreeze config so the next open gets a fresh snapshot.
    hasStartedRef.current = false;
    // animationConfigRef will be updated on next render (hasStartedRef.current is now false)
    closeAnimation();
    // Only flip the select modal when it's actually open. On desktop the
    // modal wraps the market-scan, so closing it is the right pairing. On
    // mobile (where the modal never opens), an unconditional toggle here
    // would open it — which then sticks around when the user navigates
    // back to the engagement step.
    if ((parentContext as any)?.engagementOpenSelectModal) {
      onEngagementSelectModalToggle?.();
    }
  }, [closeAnimation, onEngagementSelectModalToggle, parentContext]);

  const isConfirmingRef = React.useRef(false);
  const handleConfirm = React.useCallback(async () => {
    if (isConfirmingRef.current) return;
    isConfirmingRef.current = true;
    try {
      if (state.selectedPackageId) {
        await setCampaign(state.selectedPackageId);
        // The fulfilled reducer has now set configuration_data.campaignId and items in Redux.
        // Persist them to the backend so they survive a page refresh.
        const { session } = store.getState();
        if (session.sessionId) {
          dispatch(sessionSave({ id: session.sessionId, data: session.data }));
        }
      }
      onMarketScanPlayed?.();
      close();
    } finally {
      isConfirmingRef.current = false;
    }
  }, [state.selectedPackageId, setCampaign, close, onMarketScanPlayed, dispatch]);

  // Compact packages header when user scrolls down in display-packs stage
  React.useEffect(() => {
    const body = bodyRef.current;
    if (!body || state.stage !== 'display-packs') {
      setPackagesCompact(false);
      return;
    }
    const handleScroll = () => {
      const scrollTop = body.scrollTop;
      setPackagesCompact((prev) => {
        if (!prev && scrollTop > 100) return true;
        if (prev && scrollTop < 10) return false;
        return prev;
      });
    };
    body.addEventListener('scroll', handleScroll, { passive: true });
    return () => body.removeEventListener('scroll', handleScroll);
  }, [state.stage]);

  // Auto-start animation once packages are available (module is loaded inside a DialogContainer)
  const startRef = React.useRef(start);
  startRef.current = start;
  const hasPackages = recommendedCampaigns.length > 0;
  React.useEffect(() => {
    if (hasStartedRef.current || !hasPackages) return;
    // Snapshot the current (pre-start) config before freezing it.
    animationConfigRef.current = config;
    hasStartedRef.current = true;
    startRef.current();
  }, [hasPackages]); // eslint-disable-line react-hooks/exhaustive-deps



  // When the feature detail modal is open, resolve the engagement item +
  // active tier + highlights matching the clicked feature so that any
  // [oh] highlights ComponentRef placed inside this module's subtree picks
  // up engagementItem/engagementHighlights automatically.
  const featureModalContext = React.useMemo(() => {
    const idx = state.featureModalIdx;
    if (idx == null) return null;
    const feat = liveFeaturePack[idx];
    if (!feat) return null;
    const matchedItem = engagementItems.find((it: any) => {
      const name = it && it.name;
      if (!name) return false;
      if (typeof name === 'string') return name === feat.label;
      if (typeof name === 'object') {
        for (const k in name) {
          if (name[k] === feat.label) return true;
        }
      }
      return false;
    });
    if (!matchedItem) return null;
    const targetCampaignId = state.selectedPackageId ?? recommendedCampaigns[0]?.id;
    const liveCampaign = targetCampaignId
      ? recommendedCampaigns.find((c) => String(c.id) === String(targetCampaignId))
      : undefined;
    let activeTier: any | undefined;
    if (liveCampaign && (liveCampaign as any).default_items) {
      const defaultEntry = (liveCampaign as any).default_items.find(
        (d: any) => d.item_id === matchedItem.id,
      );
      if (defaultEntry?.tier_id != null) {
        activeTier = matchedItem.tiers?.find((t: any) => t.id === defaultEntry.tier_id);
      }
    }
    if (!activeTier) activeTier = matchedItem.tiers?.find((t: any) => t.active);
    if (!activeTier) activeTier = matchedItem.tiers?.[0];
    const highlights = (activeTier?.art_bundle ?? [])
      .filter((ab: any) => ab?.properties?.section_id === 'highlight')
      .map((ab: any) => ({
        title: ab?.properties?.title ?? {},
        description: ab?.properties?.description ?? {},
      }));
    // Override engagementSelectedVideo / engagementSelectedHtml so the [oh] preview's
    // video container (which compares its own data-key to engagementSelectedVideo.key)
    // shows the asset for the clicked feature, not the configurator session's selection.
    const engagementVideosList: any[] = (parentContext as any)?.engagementVideos ?? [];
    const selectedVideo = activeTier
      ? engagementVideosList.find(
          (v: any) => v.itemId === matchedItem.id && v.tierId === activeTier.id,
        )
      : undefined;
    const engagementHtmlList: any[] = (parentContext as any)?.engagementHtmlPreviews ?? [];
    const selectedHtml = activeTier
      ? engagementHtmlList.find(
          (h: any) => h.itemId === matchedItem.id && h.tierId === activeTier.id,
        )
      : undefined;
    return {
      engagementItem: matchedItem,
      engagementHighlights: highlights,
      engagementTier: activeTier,
      engagementSelectedVideo: selectedVideo,
      engagementSelectedHtml: selectedHtml,
    };
  }, [state.featureModalIdx, liveFeaturePack, engagementItems, recommendedCampaigns, state.selectedPackageId, parentContext]);

  // When the "Configure your own" screen is open, resolve engagement context
  // from configureFeatureIdx + configureFeatureSelections so the [oh] preview's
  // video/html and any highlights ref render the focused feature at the chosen tier.
  const configureScreenContext = React.useMemo(() => {
    if (!state.configureScreenOpen) return null;
    const matchedItem = engagementItems[state.configureFeatureIdx];
    if (!matchedItem) return null;
    const selectedTierId = state.configureFeatureSelections[String(matchedItem.id)];
    let activeTier: any | undefined;
    if (selectedTierId != null) {
      activeTier = matchedItem.tiers?.find((t: any) => String(t.id) === String(selectedTierId));
    }
    if (!activeTier) activeTier = matchedItem.tiers?.find((t: any) => t.active);
    if (!activeTier) activeTier = matchedItem.tiers?.[0];
    const highlights = (activeTier?.art_bundle ?? [])
      .filter((ab: any) => ab?.properties?.section_id === 'highlight')
      .map((ab: any) => ({
        title: ab?.properties?.title ?? {},
        description: ab?.properties?.description ?? {},
      }));
    const engagementVideosList: any[] = (parentContext as any)?.engagementVideos ?? [];
    const selectedVideo = activeTier
      ? engagementVideosList.find(
          (v: any) => v.itemId === matchedItem.id && v.tierId === activeTier.id,
        )
      : undefined;
    const engagementHtmlList: any[] = (parentContext as any)?.engagementHtmlPreviews ?? [];
    const selectedHtml = activeTier
      ? engagementHtmlList.find(
          (h: any) => h.itemId === matchedItem.id && h.tierId === activeTier.id,
        )
      : undefined;
    return {
      engagementItem: matchedItem,
      engagementHighlights: highlights,
      engagementTier: activeTier,
      engagementSelectedVideo: selectedVideo,
      engagementSelectedHtml: selectedHtml,
    };
  }, [
    state.configureScreenOpen,
    state.configureFeatureIdx,
    state.configureFeatureSelections,
    engagementItems,
    parentContext,
  ]);

  // Freeze the engagement items behind the CUSTOM pack price once the user has
  // configured it (configurePackSaved). Tier prices are campaign-scoped, so without
  // this, switching packages afterwards would recompute the custom total from a
  // different campaign's prices. Keeping a snapshot keeps it = the selected tiers.
  const customItemsSnapshotRef = React.useRef<any[]>(engagementItems);
  if (!state.configurePackSaved) {
    customItemsSnapshotRef.current = engagementItems;
  }
  const customEngagementItems = state.configurePackSaved ? customItemsSnapshotRef.current : engagementItems;

  const contextValue = React.useMemo<MarketScanContextValue>(
    () => ({
      // Spread parent context so children inherit configurator's step,
      // engagementItems, language, etc.
      ...((parentContext as any) || {}),
      // Freeze the items behind the custom total so it stays = the configured
      // tiers even when the user switches packages.
      engagementItems: customEngagementItems,
      // Override featurePack with the live one so declarative consumers see the
      // feature pack of the currently-selected package, not the frozen snapshot.
      config: { ...config, featurePack: liveFeaturePack },
      state,
      onStart: start,
      onClose: close,
      onPackageSelect: handlePackageSelect,
      onFeatureModalOpen: openFeatureModal,
      onFeatureModalClose: closeFeatureModal,
      onPackageDrawerOpen: openPackageDrawer,
      onPackageDrawerClose: closePackageDrawer,
      onConfigureScreenOpen: openConfigureScreen,
      onConfigureScreenClose: closeConfigureScreen,
      onConfigureFeatureSelect: selectConfigureFeature,
      onConfigureTierSelect: selectConfigureTier,
      onConfigureTierSeed: seedConfigureTier,
      onConfigureSelectionsClear: clearConfigureSelections,
      onMarketScanConfirm: handleConfirm,
      // When the feature modal is open, override engagementItem +
      // engagementHighlights so the [oh] highlights ComponentRef shows
      // the clicked feature's data instead of the configurator's default.
      ...((featureModalContext as any) || {}),
      // Same override when the Configure your own screen is open — drives the
      // [oh] preview's video/html and any embedded highlights ref.
      ...((configureScreenContext as any) || {}),
    }),
    [parentContext, config, liveFeaturePack, customEngagementItems, state, start, close, handlePackageSelect, openFeatureModal, closeFeatureModal, openPackageDrawer, closePackageDrawer, openConfigureScreen, closeConfigureScreen, selectConfigureFeature, selectConfigureTier, seedConfigureTier, clearConfigureSelections, handleConfirm, featureModalContext, configureScreenContext],
  );

  const showProgress = PROGRESS_STAGES.has(state.stage);
  const showCompetitors = COMPETITOR_STAGES.has(state.stage);
  const showPackages = PACKAGE_STAGES.has(state.stage);
  const isDisplayPacks = state.stage === 'display-packs';
  const isIntroOrInit = state.stage === 'intro' || state.stage === 'ui-init';

  return (
    <ModuleElementDiv className={className ?? ''} $styleText={styleText}>
      <DataElementContext.Provider value={contextValue}>
        {componentProps.children}
      </DataElementContext.Provider>
      {renderUi && (
      <div
        className={`ms-container${state.quickReveal ? ' ms-quick-reveal' : ''}`}
        data-stage={state.stage}
      >
        {isIntroOrInit && <HeroStar config={config} />}
        {(isIntroOrInit || state.quickReveal) && <GradientWaves config={config} />}
        <div
          className={`ms-content-wrapper${state.quickReveal ? ' ms-quick-reveal' : ''}`}
          style={{
            opacity: state.stage === 'intro' || state.stage === 'idle' ? 0 : 1,
            transition: 'opacity 0.6s ease',
            pointerEvents: state.stage === 'intro' || state.stage === 'idle' ? 'none' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'hidden',
            ...(state.quickReveal
              ? ({
                  ['--ms-qr-duration' as string]: `${config.quickReveal.durationMs}ms`,
                  ['--ms-qr-stagger' as string]: `${config.quickReveal.staggerMs}ms`,
                  ['--ms-qr-initial-delay' as string]: `${config.quickReveal.initialDelayMs}ms`,
                  ['--ms-qr-blur' as string]: `${config.quickReveal.blurPx}px`,
                  ['--ms-qr-translate-y' as string]: `${config.quickReveal.translateYPx}px`,
                } as React.CSSProperties)
              : {}),
          }}
        >
          <ScanHeader config={config.header} onClose={close} introActive={isIntroOrInit} />
          <div className="ms-body" ref={bodyRef}>
            <div className={`ms-scan-ui${isDisplayPacks ? ' gap-36' : ''}`}>
              {showPackages && (
                <PackagesDisplay
                  packages={config.packages}
                  revealedIds={state.revealedPackageIds}
                  shiningId={state.shiningPackageId}
                  expandedId={state.expandedPackageId}
                  selectedId={state.selectedPackageId}
                  tagsVisibleIds={state.tagsVisiblePackageIds}
                  onSelect={handlePackageSelect}
                  compact={packagesCompact}
                />
              )}
              <ProgressBar
                progress={state.progress}
                statusText={state.currentStatusText}
                statusRevealing={state.statusTextRevealing}
                visible={showProgress || isDisplayPacks}
                exiting={isDisplayPacks}
                showDot={state.stage === 'intro'}
              />
              {isDisplayPacks && state.competitorsMini ? (
                <BenchmarkBar config={liveBenchmark} visible={state.benchmarkVisible}>
                  <CompetitorGrid
                    batches={config.competitors}
                    revealedBatches={state.revealedBatches}
                    visible={showCompetitors}
                    isMini={true}
                  />
                </BenchmarkBar>
              ) : (
                <CompetitorGrid
                  batches={config.competitors}
                  revealedBatches={state.revealedBatches}
                  visible={showCompetitors}
                  isMini={false}
                  analyzed={state.stage === 'display-packs'}
                />
              )}
              {isDisplayPacks && (
                <FeaturePack
                  visible={state.featurePackVisible}
                  packName={
                    config.packages.find((p) => p.id === state.selectedPackageId)?.name ?? 'Edge'
                  }
                  items={liveFeaturePack}
                />
              )}
            </div>
          </div>
          {isDisplayPacks && (
            <SelectedPackageFooter
              visible={state.footerVisible}
              selectedPackage={
                config.packages.find((p) => p.id === state.selectedPackageId) ?? null
              }
              onConfirm={handleConfirm}
            />
          )}
        </div>
      </div>
      )}
    </ModuleElementDiv>
  );
};

export default MarketScan;
